import React, { useCallback, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import axiosInstance from './axiosInstance';

const currencyFormatter = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
});

const humanReadableSize = (sizeInBytes = 0) => {
  if (!Number.isFinite(sizeInBytes) || sizeInBytes <= 0) return '0 KB';
  const sizeInKb = sizeInBytes / 1024;
  if (sizeInKb < 1024) return `${sizeInKb.toFixed(2)} KB`;
  return `${(sizeInKb / 1024).toFixed(2)} MB`;
};

const parseErrorMessage = async (error) => {
  const fallback = 'Failed to upload the payroll report.';
  if (!error?.response) return error?.message || fallback;

  const { data } = error.response;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      try {
        const parsed = JSON.parse(text);
        return parsed?.error || parsed?.message || text || fallback;
      } catch (_parseError) {
        return text || fallback;
      }
    } catch (_blobError) {
      return fallback;
    }
  }

  return error.response?.data?.error || error.response?.data?.message || fallback;
};

const PayrollUpload = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [summary, setSummary] = useState(null);

  const onDrop = useCallback((acceptedFiles, fileRejections) => {
    if (fileRejections?.length) {
      setErrorMessage('Only .csv files are supported.');
      return;
    }

    const file = acceptedFiles[0];
    if (!file) return;
    setSelectedFile(file);
    setSuccessMessage('');
    setErrorMessage('');
    setSummary(null);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'text/csv': ['.csv'] },
    maxFiles: 1,
    multiple: false,
    onDrop,
  });

  const handleUpload = async () => {
    if (!selectedFile) {
      setErrorMessage('Please choose a payroll CSV before uploading.');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);

    setIsUploading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const response = await axiosInstance.post('/api/v1/payroll/report', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const payload = response?.data?.data ?? response?.data ?? null;
      setSummary(payload);
      const successCount = Array.isArray(payload?.successful_records) ? payload.successful_records.length : 0;
      const failedCount = Array.isArray(payload?.failed_records) ? payload.failed_records.length : 0;
      const duplicateCount = Array.isArray(payload?.duplicate_payroll_references)
        ? payload.duplicate_payroll_references.length
        : 0;
      setSuccessMessage(
        `Upload completed. ${successCount} rows saved, ${failedCount} rows skipped, ${duplicateCount} duplicate payroll reference(s) found.`
      );
      setSelectedFile(null);
    } catch (error) {
      const message = await parseErrorMessage(error);
      setErrorMessage(message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClear = () => {
    setSelectedFile(null);
    setSuccessMessage('');
    setErrorMessage('');
    setSummary(null);
  };

  const rows = useMemo(() => (Array.isArray(summary?.successful_records) ? summary.successful_records : []), [summary]);
  const failedRows = useMemo(() => (Array.isArray(summary?.failed_records) ? summary.failed_records : []), [summary]);
  const duplicateReferences = useMemo(
    () => (Array.isArray(summary?.duplicate_payroll_references) ? summary.duplicate_payroll_references : []),
    [summary]
  );

  return (
    <Box
      sx={{
        maxWidth: 1100,
        mx: 'auto',
        px: '5%',
        py: 4,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
      }}
    >
      <Typography variant="h4" gutterBottom sx={{ textAlign: 'left', width: '100%' }}>
        Upload Payroll
      </Typography>
      <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
        Upload the payroll CSV containing Job Title, Payroll Reference, Salary, and Rate.
      </Typography>

      <Box
        {...getRootProps({ className: 'dropzone' })}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.400',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          backgroundColor: isDragActive ? 'primary.light' : '#fff',
          cursor: 'pointer',
          width: '100%',
          minHeight: 180,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          transition: 'background-color 0.2s ease, border-color 0.2s ease',
          '&:hover': {
            borderColor: 'primary.main',
          },
        }}
      >
        <input {...getInputProps()} />
        <Typography variant="h6" gutterBottom>
          {isDragActive ? 'Drop the CSV file here...' : 'Drag & drop the CSV file here'}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          or click to select a file
        </Typography>
      </Box>

      {selectedFile && (
        <Box sx={{ mt: 2, width: '100%' }}>
          <Alert severity="info">
            Selected file: {selectedFile.name} ({humanReadableSize(selectedFile.size)})
          </Alert>
        </Box>
      )}

      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Button variant="contained" onClick={handleUpload} disabled={isUploading}>
          {isUploading ? 'Uploading…' : 'Upload Payroll'}
        </Button>
        <Button variant="outlined" onClick={handleClear} disabled={isUploading && !selectedFile}>
          Clear
        </Button>
      </Stack>

      {successMessage && (
        <Box sx={{ mt: 2, width: '100%' }}>
          <Alert severity="success">{successMessage}</Alert>
        </Box>
      )}

      {errorMessage && (
        <Box sx={{ mt: 2, width: '100%' }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      {rows.length > 0 && (
        <Box sx={{ mt: 3, width: '100%' }}>
          <Typography variant="h6" gutterBottom>
            Saved Rows ({rows.length})
          </Typography>
          <TableContainer component={Paper} sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Job Title</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Payroll Reference</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Salary</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Rate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={`${row?.payroll_reference || 'row'}-${index}`}>
                    <TableCell>{row?.job_title || '-'}</TableCell>
                    <TableCell>{row?.payroll_reference || '-'}</TableCell>
                    <TableCell>{currencyFormatter.format(Number(row?.salary || 0))}</TableCell>
                    <TableCell>{row?.rate || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {failedRows.length > 0 && (
        <Box sx={{ mt: 3, width: '100%' }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Failed rows: {failedRows.length}
          </Alert>
          <TableContainer component={Paper} sx={{ maxHeight: 320 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Row Number</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Job Title</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Payroll Reference</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {failedRows.map((item, index) => (
                  <TableRow key={`${item?.row_number || 'failed'}-${index}`}>
                    <TableCell>{item?.row_number || '-'}</TableCell>
                    <TableCell>{item?.job_title || '-'}</TableCell>
                    <TableCell>{item?.payroll_reference || '-'}</TableCell>
                    <TableCell>{item?.reason || 'Unknown failure'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {duplicateReferences.length > 0 && (
        <Box sx={{ mt: 3, width: '100%' }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Duplicate payroll references found in the uploaded file.
          </Alert>
          <TableContainer component={Paper} sx={{ maxHeight: 320 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Payroll Reference</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Count</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Row Numbers</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {duplicateReferences.map((item) => (
                  <TableRow key={item.payroll_reference}>
                    <TableCell>{item.payroll_reference || '-'}</TableCell>
                    <TableCell>{item.count || 0}</TableCell>
                    <TableCell>{Array.isArray(item.row_numbers) ? item.row_numbers.join(', ') : '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default PayrollUpload;
